/**
 * Voice Context
 *
 * Provides voice system state and settings throughout the app with localStorage persistence.
 * Uses a reducer pattern matching SettingsContext for consistency.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { useVoiceSettings, useUpdateVoiceSettings } from '@/api/hooks';
import type {
  VoiceSettings,
  VoiceState,
  VoiceStatus,
  OfflineContext,
  IntentResponse,
} from '@/types/voice';

// ============================================================================
// Default Settings
// ============================================================================

const defaultVoiceSettings: VoiceSettings = {
  voiceInput: {
    enabled: true,
    deviceId: null,
    language: 'en-US',
    continuous: false,
  },
  voiceOutput: {
    enabled: true,
    quality: 'auto',
    voiceId: null,
    rate: 1.0,
    pitch: 1.0,
    volume: 0.8,
  },
  wakeWord: {
    enabled: false, // OFF by default (battery warning)
    phrase: 'Hey Alfie',
    sensitivity: 'medium',
  },
  alfieEscalation: {
    autoEscalate: false,
    confirmFirst: true,
    certaintyThreshold: 0.5,
  },
  navigation: {
    enabled: false,      // Voice navigation disabled by default
    autoNavigate: false, // Always ask first (until user clicks "Always")
  },
};

const defaultVoiceState: VoiceState = {
  status: 'idle',
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  lastResponse: null,
  error: null,
  settings: defaultVoiceSettings,
  context: null,
  intentReady: false,
};

// ============================================================================
// Action Types
// ============================================================================

type VoiceAction =
  | { type: 'SET_STATUS'; payload: VoiceStatus }
  | { type: 'SET_ONLINE'; payload: boolean }
  | { type: 'SET_RESPONSE'; payload: IntentResponse | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CONTEXT'; payload: OfflineContext | null }
  | { type: 'SET_INTENT_READY'; payload: boolean }
  | { type: 'UPDATE_VOICE_INPUT'; payload: Partial<VoiceSettings['voiceInput']> }
  | { type: 'UPDATE_VOICE_OUTPUT'; payload: Partial<VoiceSettings['voiceOutput']> }
  | { type: 'UPDATE_WAKE_WORD'; payload: Partial<VoiceSettings['wakeWord']> }
  | { type: 'UPDATE_ALFIE_ESCALATION'; payload: Partial<VoiceSettings['alfieEscalation']> }
  | { type: 'UPDATE_NAVIGATION'; payload: Partial<VoiceSettings['navigation']> }
  | { type: 'LOAD_SETTINGS'; payload: Partial<VoiceSettings> }
  | { type: 'RESET_SETTINGS' };

// ============================================================================
// Local Storage
// ============================================================================

const VOICE_SETTINGS_KEY = 'prometheus-voice-settings';

function loadVoiceSettingsFromStorage(): Partial<VoiceSettings> {
  try {
    const stored = localStorage.getItem(VOICE_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load voice settings from localStorage:', error);
  }
  return {};
}

function saveVoiceSettingsToStorage(settings: VoiceSettings): void {
  try {
    localStorage.setItem(VOICE_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save voice settings to localStorage:', error);
  }
}

// ============================================================================
// Reducer
// ============================================================================

function voiceReducer(state: VoiceState, action: VoiceAction): VoiceState {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload };

    case 'SET_ONLINE':
      return { ...state, isOnline: action.payload };

    case 'SET_RESPONSE':
      return { ...state, lastResponse: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_CONTEXT':
      return { ...state, context: action.payload };

    case 'SET_INTENT_READY':
      return { ...state, intentReady: action.payload };

    case 'UPDATE_VOICE_INPUT':
      return {
        ...state,
        settings: {
          ...state.settings,
          voiceInput: { ...state.settings.voiceInput, ...action.payload },
        },
      };

    case 'UPDATE_VOICE_OUTPUT':
      return {
        ...state,
        settings: {
          ...state.settings,
          voiceOutput: { ...state.settings.voiceOutput, ...action.payload },
        },
      };

    case 'UPDATE_WAKE_WORD':
      return {
        ...state,
        settings: {
          ...state.settings,
          wakeWord: { ...state.settings.wakeWord, ...action.payload },
        },
      };

    case 'UPDATE_ALFIE_ESCALATION':
      return {
        ...state,
        settings: {
          ...state.settings,
          alfieEscalation: { ...state.settings.alfieEscalation, ...action.payload },
        },
      };

    case 'UPDATE_NAVIGATION':
      return {
        ...state,
        settings: {
          ...state.settings,
          navigation: { ...state.settings.navigation, ...action.payload },
        },
      };

    case 'LOAD_SETTINGS':
      return {
        ...state,
        settings: {
          voiceInput: { ...state.settings.voiceInput, ...action.payload.voiceInput },
          voiceOutput: { ...state.settings.voiceOutput, ...action.payload.voiceOutput },
          wakeWord: { ...state.settings.wakeWord, ...action.payload.wakeWord },
          alfieEscalation: { ...state.settings.alfieEscalation, ...action.payload.alfieEscalation },
          navigation: { ...state.settings.navigation, ...action.payload.navigation },
        },
      };

    case 'RESET_SETTINGS':
      return { ...state, settings: defaultVoiceSettings };

    default:
      return state;
  }
}

// ============================================================================
// Context Type
// ============================================================================

interface VoiceContextType {
  state: VoiceState;
  // Status actions
  setStatus: (status: VoiceStatus) => void;
  setResponse: (response: IntentResponse | null) => void;
  setError: (error: string | null) => void;
  setContext: (context: OfflineContext | null) => void;
  setIntentReady: (ready: boolean) => void;
  // Settings actions
  updateVoiceInput: (settings: Partial<VoiceSettings['voiceInput']>) => void;
  updateVoiceOutput: (settings: Partial<VoiceSettings['voiceOutput']>) => void;
  updateWakeWord: (settings: Partial<VoiceSettings['wakeWord']>) => void;
  updateAlfieEscalation: (settings: Partial<VoiceSettings['alfieEscalation']>) => void;
  updateNavigation: (settings: Partial<VoiceSettings['navigation']>) => void;
  resetSettings: () => void;
  // Convenience getters
  isListening: boolean;
  isSpeaking: boolean;
  canUseVoice: boolean;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

export function VoiceProvider({ children }: { children: React.ReactNode }) {
  // Database hooks
  const { data: dbSettings } = useVoiceSettings();
  const { mutate: updateDbSettings } = useUpdateVoiceSettings();

  // Initialize state with stored settings
  const [state, dispatch] = useReducer(voiceReducer, defaultVoiceState, (initial) => {
    const storedSettings = loadVoiceSettingsFromStorage();
    return {
      ...initial,
      settings: {
        voiceInput: { ...initial.settings.voiceInput, ...storedSettings.voiceInput },
        voiceOutput: { ...initial.settings.voiceOutput, ...storedSettings.voiceOutput },
        wakeWord: { ...initial.settings.wakeWord, ...storedSettings.wakeWord },
        alfieEscalation: { ...initial.settings.alfieEscalation, ...storedSettings.alfieEscalation },
        navigation: { ...initial.settings.navigation, ...storedSettings.navigation },
      },
    };
  });

  // On login, load DB settings and merge with localStorage (DB is source of truth)
  useEffect(() => {
    if (dbSettings) {
      const mergedSettings: VoiceSettings = {
        voiceInput: {
          enabled: dbSettings.voiceInputEnabled,
          deviceId: dbSettings.voiceInputDeviceId,
          language: dbSettings.voiceInputLanguage,
          continuous: dbSettings.voiceInputContinuous,
        },
        voiceOutput: {
          enabled: dbSettings.voiceOutputEnabled,
          quality: dbSettings.voiceOutputQuality as 'auto' | 'high' | 'low',
          voiceId: dbSettings.voiceOutputVoiceId,
          rate: dbSettings.voiceOutputRate,
          pitch: dbSettings.voiceOutputPitch,
          volume: dbSettings.voiceOutputVolume,
        },
        wakeWord: {
          enabled: dbSettings.wakeWordEnabled,
          phrase: dbSettings.wakeWordPhrase,
          sensitivity: dbSettings.wakeWordSensitivity as 'low' | 'medium' | 'high',
        },
        alfieEscalation: {
          autoEscalate: dbSettings.alfieEscalationAutoEscalate,
          confirmFirst: dbSettings.alfieEscalationConfirmFirst,
          certaintyThreshold: dbSettings.alfieEscalationCertaintyThreshold,
        },
        navigation: {
          enabled: dbSettings.navigationEnabled,
          autoNavigate: dbSettings.navigationAutoNavigate,
        },
      };

      // Seed the sync guard so the persist effect skips the immediate
      // post-load write — the DB already holds these exact values.
      lastSyncedJsonRef.current = JSON.stringify(mergedSettings);
      dispatch({ type: 'LOAD_SETTINGS', payload: mergedSettings });
      // Update localStorage cache
      saveVoiceSettingsToStorage(mergedSettings);
    }
  }, [dbSettings]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => dispatch({ type: 'SET_ONLINE', payload: true });
    const handleOffline = () => dispatch({ type: 'SET_ONLINE', payload: false });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Track the last value we've synced to the DB. We compare by JSON instead
  // of identity so that LOAD_SETTINGS dispatches (which rebuild state.settings
  // from the freshly fetched DB row) don't trigger a redundant write — that
  // write bumps synced_at, defeats React Query's structural sharing on the
  // next refetch, and would put us in an infinite refetch/dispatch loop.
  const lastSyncedJsonRef = useRef<string | null>(null);

  // Persist settings to localStorage AND sync to DB (only on real changes)
  useEffect(() => {
    saveVoiceSettingsToStorage(state.settings);

    const currentJson = JSON.stringify(state.settings);
    if (lastSyncedJsonRef.current === currentJson) return;
    lastSyncedJsonRef.current = currentJson;

    // Sync to DB (will trigger subscription on other devices)
    const flatSettings = {
      voiceInputEnabled: state.settings.voiceInput.enabled,
      voiceInputDeviceId: state.settings.voiceInput.deviceId,
      voiceInputLanguage: state.settings.voiceInput.language,
      voiceInputContinuous: state.settings.voiceInput.continuous,
      voiceOutputEnabled: state.settings.voiceOutput.enabled,
      voiceOutputQuality: state.settings.voiceOutput.quality,
      voiceOutputVoiceId: state.settings.voiceOutput.voiceId,
      voiceOutputRate: state.settings.voiceOutput.rate,
      voiceOutputPitch: state.settings.voiceOutput.pitch,
      voiceOutputVolume: state.settings.voiceOutput.volume,
      wakeWordEnabled: state.settings.wakeWord.enabled,
      wakeWordPhrase: state.settings.wakeWord.phrase,
      wakeWordSensitivity: state.settings.wakeWord.sensitivity,
      alfieEscalationAutoEscalate: state.settings.alfieEscalation.autoEscalate,
      alfieEscalationConfirmFirst: state.settings.alfieEscalation.confirmFirst,
      alfieEscalationCertaintyThreshold: state.settings.alfieEscalation.certaintyThreshold,
      navigationEnabled: state.settings.navigation.enabled,
      navigationAutoNavigate: state.settings.navigation.autoNavigate,
    };

    updateDbSettings(flatSettings);
  }, [state.settings, updateDbSettings]);

  // Actions
  const setStatus = useCallback((status: VoiceStatus) => {
    dispatch({ type: 'SET_STATUS', payload: status });
  }, []);

  const setResponse = useCallback((response: IntentResponse | null) => {
    dispatch({ type: 'SET_RESPONSE', payload: response });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const setContext = useCallback((context: OfflineContext | null) => {
    dispatch({ type: 'SET_CONTEXT', payload: context });
  }, []);

  const setIntentReady = useCallback((ready: boolean) => {
    dispatch({ type: 'SET_INTENT_READY', payload: ready });
  }, []);

  const updateVoiceInput = useCallback((settings: Partial<VoiceSettings['voiceInput']>) => {
    dispatch({ type: 'UPDATE_VOICE_INPUT', payload: settings });
  }, []);

  const updateVoiceOutput = useCallback((settings: Partial<VoiceSettings['voiceOutput']>) => {
    dispatch({ type: 'UPDATE_VOICE_OUTPUT', payload: settings });
  }, []);

  const updateWakeWord = useCallback((settings: Partial<VoiceSettings['wakeWord']>) => {
    dispatch({ type: 'UPDATE_WAKE_WORD', payload: settings });
  }, []);

  const updateAlfieEscalation = useCallback((settings: Partial<VoiceSettings['alfieEscalation']>) => {
    dispatch({ type: 'UPDATE_ALFIE_ESCALATION', payload: settings });
  }, []);

  const updateNavigation = useCallback((settings: Partial<VoiceSettings['navigation']>) => {
    dispatch({ type: 'UPDATE_NAVIGATION', payload: settings });
  }, []);

  const resetSettings = useCallback(() => {
    dispatch({ type: 'RESET_SETTINGS' });
  }, []);

  // Convenience getters
  const isListening = state.status === 'listening';
  const isSpeaking = state.status === 'speaking';
  // Voice input requires the user to have enabled it AND be online (Web Speech API needs network).
  const canUseVoice = state.settings.voiceInput.enabled && state.isOnline;

  const value: VoiceContextType = useMemo(
    () => ({
      state,
      setStatus,
      setResponse,
      setError,
      setContext,
      setIntentReady,
      updateVoiceInput,
      updateVoiceOutput,
      updateWakeWord,
      updateAlfieEscalation,
      updateNavigation,
      resetSettings,
      isListening,
      isSpeaking,
      canUseVoice,
    }),
    [
      state,
      setStatus,
      setResponse,
      setError,
      setContext,
      setIntentReady,
      updateVoiceInput,
      updateVoiceOutput,
      updateWakeWord,
      updateAlfieEscalation,
      updateNavigation,
      resetSettings,
      isListening,
      isSpeaking,
      canUseVoice,
    ]
  );

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

// Safe fallback when used outside VoiceProvider (e.g., in tests)
const noopFn = () => {};
const fallbackContext: VoiceContextType = {
  state: defaultVoiceState,
  setStatus: noopFn as any,
  setResponse: noopFn as any,
  setError: noopFn as any,
  setContext: noopFn as any,
  setIntentReady: noopFn as any,
  updateVoiceInput: noopFn as any,
  updateVoiceOutput: noopFn as any,
  updateWakeWord: noopFn as any,
  updateAlfieEscalation: noopFn as any,
  updateNavigation: noopFn as any,
  resetSettings: noopFn as any,
  canUseVoice: false,
  isListening: false,
  isSpeaking: false,
};

export function useVoice() {
  const context = useContext(VoiceContext);
  if (context === undefined) {
    return fallbackContext as any;
  }
  return context;
}

export default VoiceContext;
